import { useCallback, useMemo, useState } from 'react';
import { directedActionError, type DirectedActionError } from '../../directedErrors';

/**
 * Runs one write of a wizard step and keeps its refusal for the screen (design c4). The refusal
 * goes away with the next run or with `clearError`.
 */
export function useDirectedAction() {
  const [error, setError] = useState<DirectedActionError | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  /** Runs the write; answers null when it worked, else the kind of the refusal (also kept in `error`). */
  const attempt = useCallback(async (action: () => Promise<unknown>): Promise<DirectedActionError | null> => {
    setError(null);
    setIsRunning(true);
    try {
      await action();
      return null;
    } catch (caught) {
      console.error('Directed video: a write failed', caught);
      const kind = directedActionError(caught);
      setError(kind);
      return kind;
    } finally {
      setIsRunning(false);
    }
  }, []);

  /** Runs the write; answers true when it worked. */
  const run = useCallback(
    async (action: () => Promise<unknown>): Promise<boolean> => (await attempt(action)) === null,
    [attempt]
  );

  const clearError = useCallback(() => setError(null), []);

  return useMemo(() => ({ error, isRunning, run, attempt, clearError }), [attempt, clearError, error, isRunning, run]);
}
